import {
  type AdditionalOptType,
  type AmenityType,
  type CreatedWithinType,
  DEFAULT_FILTERS,
  FILTER_URL_KEYS,
  type FilterState,
  type LaundryType,
  type PetType,
  type PropertyType,
  type RentalType,
  type RestrictionType,
  VALID_ADDITIONAL_OPT_VALUES,
  VALID_AMENITY_VALUES,
  VALID_CREATED_WITHIN_VALUES,
  VALID_LAUNDRY_VALUES,
  VALID_ORDER_BY_VALUES,
  VALID_PET_VALUES,
  VALID_PROPERTY_TYPE_VALUES,
  VALID_RENTAL_TYPE_VALUES,
  VALID_RESTRICTION_VALUES,
} from './filterTypes';
import { expandBedroomRange, parseBedsFromUrl, serializeBedsToUrl } from './mappings/bedroomMappings';
import {
  commuteToV2Params,
  parseCommuteMinutesFromUrl,
  parseCommuteModeFromUrl,
  parseCommuteTimeModeFromUrl,
} from './mappings/commuteMappings';
import { expandBathroomRange, parseBathsFromUrl } from './mappings/bathroomMappings';
import { mapPropertyTypesToApi } from './mappings/propertyTypeMappings';
import { createdWithinToApiHours } from './mappings/createdWithinMappings';
import { restrictionToApiParams } from './mappings/restrictionMappings';
import { additionalOptsToApiParams } from './mappings/additionalOptsMappings';
import {
  normalizeMoveInDate,
  validateCoordinate,
  validateDateString,
  validateEnum,
  validateEnumList,
  validateNumeric,
  validateRange,
} from './filterValidation';

export { FILTER_URL_KEYS };

/**
 * Strips undefined, null, and empty string values from an object.
 * Preserves 0 and false.
 */
export function compactParams(
  params: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    result[key] = value as string | number | boolean;
  }
  return result;
}

/**
 * Deserializes URL query params into FilterState.
 * Accepts Next.js router.query (Record<string, string | string[] | undefined>).
 */
export function deserializeFromUrl(
  query: Record<string, string | Array<string> | undefined>,
): FilterState {
  const get = (key: string): string | undefined => {
    const val = query[key];
    if (Array.isArray(val)) return val[0];
    return val;
  };

  const filters: FilterState = { ...DEFAULT_FILTERS };

  // Price
  const price = get('price');
  if (price) {
    const [minStr, maxStr] = price.split('-');
    const min = minStr ? validateNumeric(minStr) : null;
    const max = maxStr ? validateNumeric(maxStr) : null;
    filters.price = validateRange(min, max);
  }

  // Beds
  const beds = get('beds');
  if (beds) {
    filters.bedrooms = parseBedsFromUrl(beds);
  }

  // Baths
  const baths = get('baths');
  if (baths) {
    filters.bathrooms = parseBathsFromUrl(baths);
  }

  // Pets
  const pets = get('pets');
  if (pets) {
    filters.pets = validateEnumList<PetType>(pets.split('-'), VALID_PET_VALUES).sort();
  }

  // Laundry
  const laundry = get('laundry');
  if (laundry) {
    filters.laundry = validateEnumList<LaundryType>(laundry.split('-'), VALID_LAUNDRY_VALUES).sort();
  }

  // Amenities
  const amenities = get('amenities');
  if (amenities) {
    filters.amenities = validateEnumList<AmenityType>(amenities.split('-'), VALID_AMENITY_VALUES).sort();
  }

  // Furnished
  const furnished = get('furnished');
  if (furnished === 'true') {
    filters.furnished = true;
  }

  // Property Types (all selected ≡ no filter → [])
  const propertyTypes = get('propertyTypes');
  if (propertyTypes) {
    const parsed = validateEnumList<PropertyType>(propertyTypes.split('-'), VALID_PROPERTY_TYPE_VALUES).sort();
    filters.propertyTypes = new Set(parsed).size === VALID_PROPERTY_TYPE_VALUES.size ? [] : parsed;
  }

  // Sqft
  const sqft = get('sqft');
  if (sqft) {
    const [minStr, maxStr] = sqft.split('-');
    const min = minStr ? validateNumeric(minStr) : null;
    const max = maxStr ? validateNumeric(maxStr) : null;
    filters.sqft = validateRange(min, max);
  }

  // Created Within
  const createdWithin = get('createdWithin');
  if (createdWithin) {
    filters.createdWithin = validateEnum<CreatedWithinType>(createdWithin, VALID_CREATED_WITHIN_VALUES);
  }

  // Rental Types (all selected ≡ no filter → [])
  const rentalTypes = get('rentalTypes');
  if (rentalTypes) {
    const parsed = validateEnumList<RentalType>(rentalTypes.split('-'), VALID_RENTAL_TYPE_VALUES).sort();
    filters.rentalTypes = new Set(parsed).size === VALID_RENTAL_TYPE_VALUES.size ? [] : parsed;
  }

  // Restrictions
  const restrictions = get('restrictions');
  if (restrictions) {
    filters.restrictions = validateEnum<RestrictionType>(restrictions, VALID_RESTRICTION_VALUES);
  }

  // Move-in Date (avail param: "to2026-06-01")
  const avail = get('avail');
  if (avail) {
    const [startStr, endStr] = avail.split('to');
    const start = startStr ? validateDateString(startStr) : null;
    const end = endStr ? validateDateString(endStr) : null;
    filters.moveInDate = normalizeMoveInDate(start, end);
  }

  // Keywords
  const keywords = get('keywords');
  if (keywords) {
    filters.keywords = keywords.trim();
  }

  // Additional Opts
  const additionalOpts = get('additionalOpts');
  if (additionalOpts) {
    filters.additionalOpts = validateEnumList<AdditionalOptType>(
      additionalOpts.split('-'),
      VALID_ADDITIONAL_OPT_VALUES,
    ).sort();
  }

  // Commute (legacy param names; scalar values despite the plural spelling).
  // All-or-nothing like the legacy app and the V3 API: a valid coordinate and
  // max minutes are required, else the whole filter stays off. Mode/timeMode
  // fall back to their legacy defaults when invalid.
  const coordinate = validateCoordinate(get('commuteLats'), get('commuteLons'));
  const commuteMinutes = parseCommuteMinutesFromUrl(get('commuteTime'));
  if (coordinate && commuteMinutes !== null) {
    filters.commute = {
      ...coordinate,
      name: get('commuteName')?.trim() || null,
      mode: parseCommuteModeFromUrl(get('commuteMode')),
      timeMode: parseCommuteTimeModeFromUrl(get('commuteTimeMode')),
      minutes: commuteMinutes,
    };
  }

  // OrderBy
  const orderBy = get('orderBy');
  if (orderBy) {
    filters.orderBy = validateEnum<string>(orderBy, VALID_ORDER_BY_VALUES) ?? orderBy;
  }

  return filters;
}

/**
 * Serializes FilterState to URL query params.
 * Only includes params that differ from defaults (no URL clutter).
 * Arrays are sorted alphabetically for canonical output.
 */
export function serializeToUrl(filters: FilterState): Record<string, string> {
  const params: Record<string, string> = {};

  // Price ($0 min means no minimum — normalize it away so it matches an unset min)
  const priceMin = filters.price.min === 0 ? null : filters.price.min;
  if (priceMin !== null || filters.price.max !== null) {
    params.price = `${priceMin ?? ''}-${filters.price.max ?? ''}`;
  }

  // Beds
  const beds = serializeBedsToUrl(filters.bedrooms);
  if (beds !== undefined) {
    params.beds = beds;
  }

  // Baths
  if (filters.bathrooms.min !== null) {
    params.baths = String(filters.bathrooms.min);
  }

  // Pets
  if (filters.pets.length > 0) {
    params.pets = [...filters.pets].sort().join('-');
  }

  // Laundry
  if (filters.laundry.length > 0) {
    params.laundry = [...filters.laundry].sort().join('-');
  }

  // Amenities
  if (filters.amenities.length > 0) {
    params.amenities = [...filters.amenities].sort().join('-');
  }

  // Furnished
  if (filters.furnished) {
    params.furnished = 'true';
  }

  // Property Types
  if (filters.propertyTypes.length > 0) {
    params.propertyTypes = [...filters.propertyTypes].sort().join('-');
  }

  // Sqft
  if (filters.sqft.min !== null || filters.sqft.max !== null) {
    params.sqft = `${filters.sqft.min ?? ''}-${filters.sqft.max ?? ''}`;
  }

  // Created Within
  if (filters.createdWithin !== null) {
    params.createdWithin = filters.createdWithin;
  }

  // Rental Types
  if (filters.rentalTypes.length > 0) {
    params.rentalTypes = [...filters.rentalTypes].sort().join('-');
  }

  // Restrictions
  if (filters.restrictions !== null) {
    params.restrictions = filters.restrictions;
  }

  // Move-in Date
  const moveInDate = normalizeMoveInDate(filters.moveInDate.start, filters.moveInDate.end);
  if (moveInDate.end) {
    params.avail = `to${moveInDate.end}`;
  }

  // Keywords
  if (filters.keywords.trim()) {
    params.keywords = filters.keywords.trim();
  }

  // Additional Opts
  if (filters.additionalOpts.length > 0) {
    params.additionalOpts = [...filters.additionalOpts].sort().join('-');
  }

  // Commute — the legacy V2 params double as the URL params (backward-compatible
  // shared links), plus commuteName to preserve the destination label.
  if (filters.commute) {
    for (const [key, value] of Object.entries(commuteToV2Params(filters.commute))) {
      params[key] = String(value);
    }
    if (filters.commute.name) {
      params.commuteName = filters.commute.name;
    }
  }

  // OrderBy
  if (filters.orderBy) {
    params.orderBy = filters.orderBy;
  }

  return params;
}

/**
 * Converts FilterState to flat Java API query params.
 * Returns compact params — no keys with undefined or null values.
 */
export function toApiParams(
  filters: FilterState,
): Record<string, string | number | boolean> {
  const raw: Record<string, unknown> = {};

  // Price
  if (filters.price.min !== null) raw.lowPrice = filters.price.min;
  if (filters.price.max !== null) raw.highPrice = filters.price.max;

  // Beds
  raw.bedrooms = expandBedroomRange(filters.bedrooms);

  // Baths
  raw.bathrooms = expandBathroomRange(filters.bathrooms);

  // Pets
  if (filters.pets.length > 0) raw.pets = filters.pets.join(',');

  // Laundry
  if (filters.laundry.length > 0) raw.laundry = filters.laundry.join(',');

  // Amenities
  if (filters.amenities.length > 0) raw.amenities = filters.amenities.join(',');

  // Furnished
  if (filters.furnished) raw.furnished = true;

  // Property Types
  raw.propertyTypes = mapPropertyTypesToApi(filters.propertyTypes);

  // Sqft
  if (filters.sqft.min !== null) raw.minSqft = filters.sqft.min;
  if (filters.sqft.max !== null) raw.maxSqft = filters.sqft.max;

  // Created Within
  raw.maxCreated = createdWithinToApiHours(filters.createdWithin);

  // Rental Types
  if (filters.rentalTypes.length > 0) raw.listingTypes = filters.rentalTypes.join(',');

  // Restrictions
  const restrictionParams = restrictionToApiParams(filters.restrictions);
  Object.assign(raw, restrictionParams);

  // Move-in Date
  const moveInDate = normalizeMoveInDate(filters.moveInDate.start, filters.moveInDate.end);
  if (moveInDate.end) raw.endOfAvailabilityDate = moveInDate.end;

  // Keywords
  if (filters.keywords.trim()) raw.keywords = filters.keywords.trim();

  // Additional Opts
  const additionalOptParams = additionalOptsToApiParams(filters.additionalOpts);
  Object.assign(raw, additionalOptParams);

  // Commute (legacy flat params). Saved searches exclude commute at their own
  // boundary (commute: null override in savedSearches.ts), matching legacy alerts.
  Object.assign(raw, commuteToV2Params(filters.commute));

  // OrderBy
  if (filters.orderBy) raw.orderBy = filters.orderBy;

  return compactParams(raw);
}
